import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../_core/_services/notification.service';
import { OrderDraftService } from '../../_core/_services/order-draft-service';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../_core/_services/api.service';
import { AuthService } from '../../_core/_services/auth.service';
import { OrderDetailsDto, OrderDto, OrderLineageDto } from '../../_core/_model/order';
import { FormatService } from '../../_core/_services/format';

@Component({
  selector: 'app-manage-order',
  standalone: false,
  templateUrl: './manage-order.html',
  styleUrl: './manage-order.css',
})
export class ManageOrder implements OnInit {
  orderData!: OrderDetailsDto;
  loading = false;
  error = '';

  newRate: number | null = null;
  updatingRate = false;
  cancellingOrder = false;

  childOrders: OrderDto[] = [];
  loadingChildOrders = false;

  orderLineage: OrderLineageDto[] = [];
  loadingLineage = false;

  isAdminPage: boolean = false;
  page = "Orders";

  orderId!: string;

  backButtonRoute: string | null = null;
  backButtonId: string | null = null;

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private format: FormatService,
    private router: Router,
    private route: ActivatedRoute,
    private draft: OrderDraftService,
    private notifyService: NotificationService,
  ) { }

  ngOnInit(): void {
    this.isAdminPage = this.route.snapshot.data['area'] === 'admin';
    if (this.isAdminPage) {
      this.page = "Admin";
    }

    this.draft.pop();
    const extra = this.route.snapshot.paramMap.get('extra') ?? null;
    const extraId = this.route.snapshot.paramMap.get('extraId') ?? null;

    if (extra !== null && extraId !== null) {
      this.backButtonRoute = extra + "/manage";
      this.backButtonId = extraId;
    }

    this.orderId = this.route.snapshot.paramMap.get('orderId') ?? '';
    if (!this.orderId) {
      this.router.navigate(['/orders']);
      this.notifyService.showError('No order specified', "Error");
    }
    this.loadOrder();
    this.auth.getCurrentUser().subscribe();
  }

  loadChildOrders() {
    if (!this.orderId) return;

    this.loadingChildOrders = true;

    this.api.getChildOrders(this.orderId).subscribe({
      next: (res) => {
        this.childOrders = res.data || [];
        this.loadingChildOrders = false;
      },
      error: () => {
        this.childOrders = [];
        this.loadingChildOrders = false;
      }
    });
  }

  loadOrderLineage() {
    if (!this.orderId) return;

    this.loadingLineage = true;

    const req = this.isAdminPage
      ? this.api.adminGetOrderLineage(this.orderId)
      : this.api.getOrderLineage(this.orderId);

    req.subscribe({
      next: (res) => {
        this.orderLineage = res.data || [];
        this.loadingLineage = false;
      },
      error: () => {
        this.orderLineage = [];
        this.loadingLineage = false;
      }
    });
  }

  openLineageOrder(orderId: string) {
    this.router.navigate(['/orders/manage', orderId]);
  }

  openChildOrder(orderId: string) {
    this.router.navigate(['/orders/manage', orderId]);
  }

  loadOrder() {
    this.loading = true;
    this.api.getOrder(this.orderId).subscribe({
      next: (d) => {
        this.orderData = d.data!;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.Error?.Details || 'Failed to load order details';
        this.notifyService.showError(this.error, "Error");
        this.loading = false;
      },
    });
  }

  goBack() {
    if (this.isAdminPage) {
      this.router.navigate(['/admin/orders']);
    } else {
      if (this.backButtonRoute !== null && this.backButtonId !== null) {
        this.router.navigate([this.backButtonRoute, this.backButtonId]);
        return;
      }
      this.router.navigate(['/orders']);
    }
  }

  cancelOrder() {

    if (!confirm(
      'Are you sure you want to cancel the remaining open portion of this order?'
    )) {
      return;
    }

    this.cancellingOrder = true;

    this.api.cancelOrder(this.orderId).subscribe({
      next: () => {

        this.notifyService.showSuccess(
          'Order cancelled successfully',
          'Success'
        );

        this.cancellingOrder = false;

        this.loadOrder();
      },
      error: (err) => {

        const message =
          err.error?.Error?.Details ||
          'Failed to cancel order';

        this.notifyService.showError(
          message,
          'Error'
        );

        this.cancellingOrder = false;
      }
    });
  }

  progrwessPercent(): number {
    if (!this.orderData || this.orderData.originalAmount === 0) return 0;
    const filled = this.orderData.originalAmount - this.orderData.remainingAmount;
    return (filled / this.orderData.originalAmount) * 100;
  }

  getOrderStatusClass(status: string): string {
    return this.format.getOrderStatusClass(status);
  }

  getUserStatusClass(status: string): string {
    return this.format.getUserStatusClass(status);
  }

  getHoldStatusClass(status: string): string {
    return this.format.getHoldStatusClass(status);
  }

  canManageOrder(): boolean {
    if (!this.orderData) return false;

    return this.orderData.status === 'Open'
      || this.orderData.status === 'Partial';
  }

  updateRate() {
    if (!this.newRate || this.newRate <= 0) {
      this.notifyService.showError(
        'Please enter a valid rate.',
        'Error'
      );
      return;
    }

    this.updatingRate = true;

    this.api.updateOrderRate(
      this.orderId,
      this.newRate
    ).subscribe({
      next: (res) => {

        this.orderData = res.data!;

        this.notifyService.showSuccess(
          'Order rate updated successfully.',
          'Success'
        );

        this.newRate = null;
        this.updatingRate = false;

        this.loadOrder();
      },
      error: (err) => {

        const message =
          err.error?.Error?.Details ||
          'Failed to update order rate';

        this.notifyService.showError(
          message,
          'Error'
        );

        this.updatingRate = false;
      }
    });
  }

  isRateChangedParent(): boolean {
    return this.orderData?.status === 'RateChanged';
  }

  isPartiallyCancelled(): boolean {
    return this.orderData?.status === 'PartiallyCancelled';
  }

  hasParentOrder(): boolean {
    return !!this.orderData?.parentOrderId;
  }

  openParentOrder() {
    if (!this.orderData?.parentOrderId) return;
    this.router.navigate(['/orders/manage', this.orderData.parentOrderId]);
  }
}
